import React, { useState, useEffect, useContext, useRef, useMemo, memo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import GaugeChart from 'react-gauge-chart';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

// Component con cho GaugeChart, được memoized
const GaugeDisplay = memo(({ id, value, unit, maxValue = 100, title, warning, isOverThreshold }) => {
  console.log(`${title} Gauge rendered with value: ${value}`); // Debug render
  return (
    <div className="bg-white p-4 rounded shadow">
      <h4 className="text-lg font-semibold mb-2">{title}</h4>
      <GaugeChart
        id={id}
        nrOfLevels={20}
        percent={value ? value / maxValue : 0}
        textColor="#000000"
        formatTextValue={() => `${(value || 0).toFixed(2)}${unit}`} // Định dạng 2 chữ số thập phân
        animate={false} // Tắt animation để tránh nhấp nháy
      />
      {isOverThreshold && (
        <p className="text-red-500 mt-2">{warning}</p>
      )}
    </div>
  );
});

const TrafficLightDetail = () => {
  const { id } = useParams();
  const [trafficLight, setTrafficLight] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const { token } = useContext(AuthContext);
  const prevDataRef = useRef(null);

  const [modemSignalHistory, setModemSignalHistory] = useState([]);
  const [lightStatusHistory, setLightStatusHistory] = useState([]);

  const API_URL = `https://dpt.saigoniot.vn:444/api/Device/getTraficLightDeviceById?id=${id}`;

  // Memoize trafficLight để tránh tạo tham chiếu mới
  const memoizedTrafficLight = useMemo(() => trafficLight, [trafficLight]);

  useEffect(() => {
    let isMounted = true;
    console.log('TrafficLightDetail rendered with id:', id); // Debug render

    const fetchTrafficLightDetail = async () => {
      if (!token) {
        if (isMounted) {
          setError('Không tìm thấy token. Vui lòng đăng nhập lại.');
          setLoading(false);
        }
        return;
      }

      try {
        setLoading(true);
        const response = await fetch(API_URL, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          if (response.status === 401) {
            throw new Error('Token không hợp lệ. Vui lòng đăng nhập lại.');
          }
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const result = await response.json();
        if (result.status !== 1) {
          throw new Error(result.error || 'Lỗi khi lấy dữ liệu đèn.');
        }

        const data = result.data;
        console.log('Dữ liệu từ API:', data);

        // So sánh với dữ liệu cũ để tránh cập nhật không cần thiết
        if (JSON.stringify(prevDataRef.current) !== JSON.stringify(data)) {
          if (isMounted) {
            setTrafficLight(data);
            setError('');

            const mockModemSignalHistory = [
              { time: '10:00', signal: data.modemSignal || 0 },
              { time: '10:05', signal: (data.modemSignal || 0) + 5 },
              { time: '10:10', signal: (data.modemSignal || 0) - 3 },
              { time: '10:15', signal: (data.modemSignal || 0) + 2 },
            ];
            setModemSignalHistory(mockModemSignalHistory);

            const mockLightStatusHistory = [
              { time: '10:00', laneA: data.laneAStatus === 'Bật' ? 1 : 0, laneB: data.laneBStatus === 'Bật' ? 1 : 0 },
              { time: '10:05', laneA: data.laneAStatus === 'Bật' ? 0 : 1, laneB: data.laneBStatus === 'Bật' ? 0 : 1 },
              { time: '10:10', laneA: data.laneAStatus === 'Bật' ? 1 : 0, laneB: data.laneBStatus === 'Bật' ? 1 : 0 },
              { time: '10:15', laneA: data.laneAStatus === 'Bật' ? 0 : 1, laneB: data.laneBStatus === 'Bật' ? 0 : 1 },
            ];
            setLightStatusHistory(mockLightStatusHistory);

            prevDataRef.current = data;
          }
        }
      } catch (err) {
        if (isMounted) {
          setError(`Lỗi khi lấy chi tiết đèn: ${err.message}`);
          console.error('Fetch error:', err);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchTrafficLightDetail();

    // Cleanup khi unmount
    return () => {
      isMounted = false;
    };
  }, [id, token]);

  return (
    <div className="flex-1 p-4 overflow-y-auto">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold">Chi tiết đèn giao thông</h2>
        <Link
          to="/traffic-lights"
          className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
        >
          Quay lại
        </Link>
      </div>

      {loading && <div className="text-gray-500">Đang tải...</div>}
      {error && <div className="text-red-500 mb-4">{error}</div>}
      {memoizedTrafficLight && !loading && (
        <div className="space-y-6">
          {/* Thông tin cơ bản */}
          <div className="bg-white p-4 rounded shadow">
            <h3 className="text-xl font-semibold mb-2">{memoizedTrafficLight.deviceName || 'N/A'}</h3>
            <p><strong>Khu vực: </strong>{memoizedTrafficLight.areaName || 'N/A'}</p>
            <p><strong>Trạng thái online: </strong>{memoizedTrafficLight.onlineStatus || 'N/A'}</p>
            <p><strong>Chế độ: </strong>{memoizedTrafficLight.controlMode || 'N/A'}</p>
            <p><strong>Chu kỳ (R/Y/G): </strong>{memoizedTrafficLight.redTime}/{memoizedTrafficLight.yellowTime}/{memoizedTrafficLight.greenTime}</p>
            <p><strong>Tọa độ: </strong>({memoizedTrafficLight.latitude}, {memoizedTrafficLight.longitude})</p>
          </div>

          {/* Dòng trên: 3 cột (Nhiệt độ, Độ ẩm, Trạng thái cửa) */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Nhiệt độ */}
            <GaugeDisplay
              id="temperature-gauge"
              value={memoizedTrafficLight.boxTemperature}
              unit="°C"
              title="Nhiệt độ"
              warning="Cảnh báo: Nhiệt độ vượt ngưỡng!"
              isOverThreshold={memoizedTrafficLight.isOverTemperature}
            />

            {/* Độ ẩm */}
            <GaugeDisplay
              id="humidity-gauge"
              value={memoizedTrafficLight.boxHumidity}
              unit="%"
              title="Độ ẩm"
              warning="Cảnh báo: Độ ẩm vượt ngưỡng!"
              isOverThreshold={memoizedTrafficLight.isOverHumidity}
            />

            {/* Trạng thái cửa tủ */}
            <div className="bg-white p-4 rounded shadow flex flex-col items-center justify-center">
              <h4 className="text-lg font-semibold mb-2">Trạng thái cửa tủ</h4>
              <div
                className={`w-16 h-16 rounded-full flex items-center justify-center text-white font-bold ${
                  memoizedTrafficLight.cabinetDoorStatus === 0 ? 'bg-green-500' : 'bg-red-500'
                }`}
              >
                {memoizedTrafficLight.cabinetDoorStatus === 0 ? 'Đóng' : 'Mở'}
              </div>
            </div>
          </div>

          {/* Dòng dưới: 2 cột (Biểu đồ 4G, Trạng thái đèn) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Biểu đồ tín hiệu 4G */}
            <div className="bg-white p-4 rounded shadow">
              <h4 className="text-lg font-semibold mb-2">Tín hiệu 4G</h4>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={modemSignalHistory}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="time" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="signal" stroke="#8884d8" activeDot={{ r: 8 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Biểu đồ trạng thái đèn */}
            <div className="bg-white p-4 rounded shadow">
              <h4 className="text-lg font-semibold mb-2">Trạng thái đèn</h4>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={lightStatusHistory}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="time" />
                  <YAxis domain={[0, 1]} ticks={[0, 1]} tickFormatter={(value) => (value === 1 ? 'Bật' : 'Tắt')} />
                  <Tooltip formatter={(value) => (value === 1 ? 'Bật' : 'Tắt')} />
                  <Legend />
                  <Line type="step" dataKey="laneA" stroke="#82ca9d" name="Lane A" />
                  <Line type="step" dataKey="laneB" stroke="#ff7300" name="Lane B" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Dòng cuối: Lịch sử sự kiện */}
          <div className="bg-white p-4 rounded shadow">
            <h4 className="text-lg font-semibold mb-2">Lịch sử sự kiện</h4>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-200">
                    <th className="border p-2">Thời gian</th>
                    <th className="border p-2">Sự kiện</th>
                    <th className="border p-2">Trạng thái</th>
                  </tr>
                </thead>
                <tbody align="center">
                  {[
                    { time: memoizedTrafficLight.lastTimeSettingCheck, event: 'Kiểm tra cài đặt gần nhất', status: 'Thành công' },
                    { time: memoizedTrafficLight.lastTimeDataPost, event: 'Cập nhật dữ liệu gần nhất', status: memoizedTrafficLight.onlineStatus || 'N/A' },
                    { time: memoizedTrafficLight.updateTime, event: 'Cập nhật thông tin', status: `bởi ${memoizedTrafficLight.updateDeviceUser || 'N/A'}` },
                    { time: memoizedTrafficLight.createTime, event: 'Tạo thiết bị', status: `bởi ${memoizedTrafficLight.createDeviceUser || 'N/A'}` },
                  ].map((log, index) => (
                    <tr key={index}>
                      <td className="border p-2">{log.time || 'N/A'}</td>
                      <td className="border p-2">{log.event}</td>
                      <td className="border p-2">{log.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      {!memoizedTrafficLight && !loading && !error && (
        <div className="text-gray-500">Không tìm thấy thông tin đèn.</div>
      )}
    </div>
  );
};

export default React.memo(TrafficLightDetail);